import React from "react";
import { getFullName } from "../../shared/utils/get-full-name";
import {
  declareAiCapability,
  isPermittedForAi,
  setAiContextEntity,
  useProvideAiCapability,
} from "../ai-omnibar/ai-capability-registry";
import { useEntityProfileSectionAiCapability } from "../entityProfileModal/hooks/useEntityProfileSectionAiCapability";
import { patientProfileBridgeActions } from "./patientProfileModal.actions";
import { patientProfileSections, PatientProfileSection } from "./patientProfileModal.sections";
import { PatientProfilePatient } from "./patientProfileModal.types";

/**
 * What the patient profile can do for the AI omnibar.
 *
 * There are two windows implementing this profile - the legacy AngularJS one (`patientCtrl.js`) and
 * the React one, chosen per user by the beta profile window setting - so the *description* lives
 * here, once, and each window merely provides the same ids while it is the one on screen.
 *
 * `scope: "patient-profile"` is all these say about how they are reached. Whatever declares
 * `opens: ["patient-profile"]` - today `patient.openModal` in config.js, whichever window it routes
 * to - makes them reachable, and neither side names the other.
 */

declareAiCapability({
  id: "patient.section.open",
  title: "Go to a section of the patient profile",
  effect: "navigate",
  description:
    "Scrolls the open patient profile to one of its sections or subsections - calendar, list view, weekly template, profile details, medical, billing, administrative and so on.",
  location: "the section navigation of the patient profile",
  scope: "patient-profile",
  params: [
    {
      name: "section",
      type: "enum",
      required: true,
      description: "Which section of the profile to show.",
      // Declared statically so the model has the vocabulary while planning - before any profile is
      // open, nothing has contributed anything yet. Filtered by the same permission keys the
      // navigation honours. The mounted profile then contributes the exact set visible for that
      // record, and the runner resolves against those.
      options: () =>
        patientProfileSections.flatMap((section) =>
          section.subsections
            .filter((subsection) => isPermittedForAi(subsection.permissionKey))
            .map((subsection) => ({
              value: `${section.label}/${subsection.label}`,
              label:
                section.label === subsection.label
                  ? section.label
                  : `${section.label} > ${subsection.label}`,
            }))
        ),
    },
  ],
  examples: ["show DeNiro's authorizations", "open the weekly template for patient 91111"],
});

declareAiCapability({
  id: "patient.newVisit.open",
  title: "Start a new visit for the patient that is open",
  effect: "prefill",
  description:
    "Opens the New Visit side bar of the patient profile, prefilled with that patient's address, contracts, languages, phone numbers, vacations and authorizations, so the user only has to pick the days, times and caregiver. Nothing is saved - the user still fills in the shift and confirms.",
  location: "the side bar of the patient profile",
  scope: "patient-profile",
  // The key the profile header checks before it offers New Visit - see PatientProfileHeader.tsx.
  permissionKey: "allow_patient_page_new_visit",
  params: [
    {
      name: "date",
      type: "date",
      required: false,
      description:
        "The day the visit should start on, as yyyy-MM-dd. Leave it out when the user did not say when.",
    },
  ],
  examples: ["create a new visit for patient 2", "schedule a visit for Jane Doe tomorrow"],
});

declareAiCapability({
  id: "patient.notes.open",
  title: "Open the notes of the patient that is open",
  effect: "navigate",
  description:
    "Opens the Notes side bar of the patient profile, showing the notes recorded about that patient and letting the user add one.",
  location: "the side bar of the patient profile",
  scope: "patient-profile",
  examples: ["show me the notes for patient 2"],
});

declareAiCapability({
  id: "patient.newTask.open",
  title: "Start a new task for the patient that is open",
  effect: "prefill",
  description:
    "Opens the new patient task form, optionally on a given day. The user picks the task type and confirms; nothing is scheduled until they do.",
  location: "a dialog over the patient profile",
  scope: "patient-profile",
  permissionKey: "view_patient_page_patient_tasks",
  params: [
    {
      name: "date",
      type: "date",
      required: false,
      description: "Day the task should be scheduled for, as yyyy-MM-dd.",
    },
  ],
  examples: ["new task for DeNiro on Friday"],
});

declareAiCapability({
  id: "patient.vacation.open",
  title: "Record a vacation for the patient that is open",
  effect: "prefill",
  description:
    "Opens the new vacation form on the patient's calendar, where the user sets the dates and confirms. It does not save the vacation.",
  location: "a dialog over the patient profile calendar",
  scope: "patient-profile",
  permissionKey: "view_patient_page_calendar",
  params: [
    {
      name: "date",
      type: "date",
      required: false,
      description: "First day of the vacation, as yyyy-MM-dd.",
    },
  ],
  examples: ["DeNiro is away next week", "add a vacation for patient 91111"],
});

declareAiCapability({
  id: "patient.calendarNote.open",
  title: "Add a calendar note for the patient that is open",
  effect: "prefill",
  description:
    "Opens the calendar note form for a day on the patient's calendar. The user writes the note and confirms.",
  location: "a dialog over the patient profile calendar",
  scope: "patient-profile",
  permissionKey: "view_patient_page_calendar",
  params: [
    {
      name: "date",
      type: "date",
      required: false,
      description: "Day the note belongs to, as yyyy-MM-dd.",
    },
  ],
  examples: ["calendar note for DeNiro tomorrow"],
});

declareAiCapability({
  id: "patient.taskActivity.open",
  title: "Show task activity for the patient that is open",
  effect: "navigate",
  description:
    "Opens the task activity drawer for this patient - what has been done on their tasks, by whom and when.",
  location: "a drawer over the patient profile",
  scope: "patient-profile",
  permissionKey: "view_patient_page_patient_tasks",
  examples: ["task activity for DeNiro"],
});

type SectionNavigation = {
  handleSectionChange: (index: number) => void;
  handleSubsectionChange: (index: number) => void;
};

/**
 * Provides the capabilities above from the React profile window, for as long as it is showing a
 * loaded patient. `patient === undefined` means the profile is still fetching, so they report
 * themselves unavailable rather than opening a form with nothing in it.
 */
export function usePatientProfileAiCapabilities(
  patient: PatientProfilePatient | undefined,
  navigation: SectionNavigation,
  visibleSections: PatientProfileSection[]
): void {
  const isReady = patient !== undefined;
  const requirePatient = () => {
    if (patient === undefined) {
      throw new Error("The patient profile is still loading.");
    }

    return patient;
  };

  const asDate = (value: unknown) => (typeof value === "string" ? value : undefined);

  useEntityProfileSectionAiCapability({
    capabilityId: "patient.section.open",
    sections: visibleSections,
    navigation,
    enabled: isReady,
  });

  useProvideAiCapability(
    "patient.newVisit.open",
    (args) => {
      const forWhom = requirePatient();

      patientProfileBridgeActions.openNewVisit({
        patient: forWhom,
        calendarDate: asDate(args.date),
      });

      return args.date === undefined
        ? `Opened New Visit for ${getFullName(forWhom)}`
        : `Opened New Visit for ${getFullName(forWhom)} on ${args.date}`;
    },
    { enabled: isReady }
  );

  useProvideAiCapability(
    "patient.notes.open",
    () => {
      const forWhom = requirePatient();

      patientProfileBridgeActions.openNotes({ patient: forWhom });

      return `Opened notes for ${getFullName(forWhom)}`;
    },
    { enabled: isReady }
  );

  useProvideAiCapability(
    "patient.newTask.open",
    (args) => {
      const forWhom = requirePatient();

      patientProfileBridgeActions.openNewTask({
        patient: forWhom,
        calendarDate: asDate(args.date),
      });

      return `Opened a new task for ${getFullName(forWhom)}`;
    },
    { enabled: isReady }
  );

  useProvideAiCapability(
    "patient.vacation.open",
    (args) => {
      const forWhom = requirePatient();

      patientProfileBridgeActions.openNewVacation({
        patient: forWhom,
        calendarDate: asDate(args.date),
      });

      return `Opened a new vacation for ${getFullName(forWhom)}`;
    },
    { enabled: isReady }
  );

  useProvideAiCapability(
    "patient.calendarNote.open",
    (args) => {
      const forWhom = requirePatient();

      patientProfileBridgeActions.openNewCalendarNote({
        patient: forWhom,
        calendarDate: asDate(args.date),
      });

      return `Opened a calendar note for ${getFullName(forWhom)}`;
    },
    { enabled: isReady }
  );

  useProvideAiCapability(
    "patient.taskActivity.open",
    () => {
      const forWhom = requirePatient();

      patientProfileBridgeActions.openTaskActivity({ patientId: forWhom.id });

      return `Opened task activity for ${getFullName(forWhom)}`;
    },
    { enabled: isReady }
  );

  // Tell the omnibar which patient is on screen, so "this patient" resolves without a search.
  React.useEffect(() => {
    if (patient === undefined) {
      return;
    }

    return setAiContextEntity(
      "patient-profile",
      `Patient profile open: ${getFullName(patient)} (id ${patient.displayId ?? patient.id})`
    );
  }, [patient]);
}
